// src/Games/PhaserGame/PhaserGame.js
import React, { useEffect, useRef, useState } from "react";
import Phaser from "phaser";
import { Link } from "react-router-dom";
import "./PhaserGame.css";

function PhaserGame() {
  const gameRef = useRef(null);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [level, setLevel] = useState(1);

  useEffect(() => {
    if (gameRef.current) return;

    let player,
      aliens,
      bullets,
      alienBullets,
      cursors,
      spaceKey,
      shootSound,
      explosionSound,
      bgm,
      powerUps,
      background,
      boss,
      bossHP = 0,
      trackingGroup;

    let lastFired = 0;
    let lastAlienShot = 0;
    let lastBossShot = 0;

    let scoreValue = 0;
    let alienSpeed = 60;
    let killCount = 0;
    let fireMultiplier = 1;
    let trackingMissiles = 0;

    /**
     * Because external sprite URLs keep failing for you,
     * we store the boss below as a base64 data URI. 
     * The missile will reuse the "bullet" sprite from labs.
     */
    const base64Boss = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAC0AAAAoCAYAAADKBlc9AAAABmJLR0QA/wD/
AP+gvaeTAAABNUlEQVRYhe2V0Q7CMBBF76TH1g7X3WMcKPvZrCBVhJI1DKWzeMx3I6mBIqB+Aku0oC6yUk42FNQvR5yfjzvvI5HO76zHA/XyMpuwcLkqT7wTG1n7O3oT4twXMezCEQ5qBv+THa2k35vwgm0vVM1a9oIDYgB8w9C8Xl7ZY5I8zEr6gjmZULGhMdBHzw4wDUGfY5TWAH4/
pbnscpfuInjDG27lkEylxH1wtQH+lQd8p3pTTCh1/vdI9D4szHmjBE0FxqfE3n/5iPo/I8KEAxykSbrYwH4pSzv5En4LoTYO6+CDQypm9gWyN7L7+vKX9auCwLrhp6fNHwdkctOmb6SXrcAAAAASUVORK5CYII=`;

    /** Phaser config */
    const config = {
      type: Phaser.AUTO,
      width: 800,
      height: 600,
      backgroundColor: "#000",
      physics: {
        default: "arcade",
        arcade: { gravity: { y: 0 }, debug: false }
      },
      scene: { preload, create, update },
      parent: "phaser-container"
    };

    gameRef.current = new Phaser.Game(config);

    /**
     * Preload: 
     * We'll load all normal assets from labs,
     * The boss is from base64, 
     * The missile reuses the bullet image from labs, bigger scale in code.
     */
    function preload() {
      console.log("Preload: Loading assets...");

      // Basic assets from labs
      this.load.image("space", "https://labs.phaser.io/assets/skies/deep-space.jpg");
      this.load.image("ship", "https://labs.phaser.io/assets/sprites/player.png");
      this.load.image("alien", "https://labs.phaser.io/assets/sprites/ufo.png");
      this.load.image("bullet", "https://labs.phaser.io/assets/sprites/bullet.png");
      this.load.image("alienBullet", "https://labs.phaser.io/assets/sprites/blue_ball.png");
      this.load.image("power", "https://labs.phaser.io/assets/sprites/shinyball.png");

      // Boss from base64
      this.textures.addBase64("boss", base64Boss);

      // We'll also load the same bullet sprite as "tracking"
      // so that missiles can use it but be scaled 1.5 times in code
      this.load.image("tracking", "https://labs.phaser.io/assets/sprites/bullet.png");

      // Audio
      this.load.audio("shoot", "https://labs.phaser.io/assets/audio/SoundEffects/shot1.wav");
      this.load.audio("explode", "https://labs.phaser.io/assets/audio/SoundEffects/explosion.mp3");
      this.load.audio("bgm", "https://labs.phaser.io/assets/audio/oedipus_wizball_highscore.mp3");
    }

    function create() {
      console.log("Create: Setup scene & environment...");
      background = this.add.tileSprite(400, 300, 800, 600, "space");

      // Groups
      player = this.physics.add.sprite(400, 550, "ship").setCollideWorldBounds(true);
      bullets = this.physics.add.group();
      alienBullets = this.physics.add.group();
      aliens = this.physics.add.group();
      powerUps = this.physics.add.group();
      trackingGroup = this.physics.add.group();

      cursors = this.input.keyboard.createCursorKeys();
      spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

      shootSound = this.sound.add("shoot");
      explosionSound = this.sound.add("explode");
      bgm = this.sound.add("bgm", { loop: true, volume: 0.3 });
      bgm.play();

      this.physics.add.overlap(bullets, aliens, hitAlien);
      this.physics.add.overlap(trackingGroup, aliens, hitAlien);
      this.physics.add.overlap(player, alienBullets, () => loseLife(this));
      this.physics.add.overlap(player, powerUps, collectPowerUp);
      this.physics.add.collider(aliens, player, () => loseLife(this));

      console.log("Create: spawning first wave at level=", level);
      spawnAliens(this);
    }

    function update(time) {
      if (gameOver) return;

      background.tilePositionY -= 1;

      // Movement
      if (cursors.left.isDown) {
        player.setVelocityX(-300);
      } else if (cursors.right.isDown) {
        player.setVelocityX(300);
      } else {
        player.setVelocityX(0);
      }

      // Shooting
      if (Phaser.Input.Keyboard.JustDown(spaceKey) && time > lastFired + 300) {
        console.log("update: Player shooting => bullets + missiles");
        const spacing = 10;

        // normal bullet spray
        for (let i = 0; i < fireMultiplier * 2; i++) {
          const offset = (i - fireMultiplier + 1) * spacing;
          fireBullet(player.x + offset, player.y);
        }

        // tracking missiles
        for (let i = 0; i < trackingMissiles; i++) {
          const missile = trackingGroup.create(player.x, player.y - 20, "tracking");
          // scale the missile 1.5x bigger
          missile.setScale(1.5);

          const targets = aliens.getChildren().filter(a => a.active);

          if (targets.length > 0) {
            const target = Phaser.Utils.Array.GetRandom(targets);
            gameRef.current.scene.scenes[0].physics.moveToObject(missile, target, 300);
          } else if (boss) {
            gameRef.current.scene.scenes[0].physics.moveToObject(missile, boss, 300);
          } else {
            missile.setVelocityY(-300);
          }
        }
        shootSound.play();
        lastFired = time;
      }

      // bullet cleanup
      bullets.children.iterate(b => {
        if (b && b.y < 0) b.destroy();
      });
      alienBullets.children.iterate(b => {
        if (b && b.y > 600) b.destroy();
      });
      trackingGroup.children.iterate(m => {
        if (m && m.y < 0) m.destroy();
      });

      // => ALIEN SHOOT LOGIC (L5+)
      if (level >= 5 && time > lastAlienShot + 1000 && aliens.countActive(true) > 0) {
        console.log("update: L5+ => alien tries to shoot => level=", level);
        const shooters = aliens.getChildren().filter(a => a.active);
        if (shooters.length > 0) {
          const shooter = Phaser.Utils.Array.GetRandom(shooters);
          const shot = alienBullets.create(shooter.x, shooter.y + 10, "alienBullet");
          shot.setVelocityY(200);
          console.log("update: alien shot from x=", shooter.x);
          lastAlienShot = time;
        }
      }

      // => BOSS SHOOT
      if (boss && time > lastBossShot + 1500) {
        console.log("update: boss firing => bossHP=", bossHP);
        const shot = alienBullets.create(boss.x, boss.y + 10, "alienBullet");
        shot.setVelocityY(300);
        lastBossShot = time;

        if (bossHP <= 0) {
          console.log("update: BOSS HP depleted => destroyed");
          explosionSound.play();
          scoreValue += 100;
          setScore(scoreValue);
          boss.destroy();
          boss = null;
          const power = powerUps.create(400, 100, "power");
          power.setVelocityY(100);
          spawnAliens(gameRef.current.scene.scenes[0]);
        } else {
          bossHP--;
        }
      }
    }

    // HELPER: showMiniBoss
    function showMiniBoss(lvl) {
      return lvl % 3 === 0 && lvl !== 10;
    }

    // HELPER: spawnAliens
    function spawnAliens(scene, currentLevel = level) {
      console.log("spawnAliens: clearing old wave => newLevel=", currentLevel);

      aliens.clear(true, true);
      alienBullets.clear(true, true);
      trackingGroup.clear(true, true);

      if (boss) {
        console.log("spawnAliens: removing old boss");
        boss.destroy();
        boss = null;
      }

      // Final Boss at L10
      if (currentLevel === 10) {
        console.log("spawnAliens: spawning final boss => L10");
        boss = scene.physics.add
          .sprite(400, 100, "alien") 
          .setScale(4) // 4x bigger
          .setTint(0xff0000)
          .setImmovable(true);
        bossHP = 50;
        boss.setVelocityX(100);
        boss.setCollideWorldBounds(true);
        boss.setBounce(1, 0);
        return;
      }

      // Mini-Boss => L3,6,9
      if (showMiniBoss(currentLevel)) {
        console.log("spawnAliens: spawning MINI BOSS => level", currentLevel);
        boss = scene.physics.add
          .sprite(400, 100, "alien") 
          .setScale(2) // 2x bigger
          .setTint(0xffff00)
          .setImmovable(true);
        bossHP = 20;
        boss.setVelocityX(80);
        boss.setCollideWorldBounds(true);
        boss.setBounce(1, 0);
        return;
      }

      // Normal Aliens wave
      for (let i = 0; i < 6; i++) {
        const x = 100 + i * 100;
        const alien = aliens.create(x, 0, "alien");
        alien.setCollideWorldBounds(true);
        alien.setBounce(1);
        alien.setVelocityY(alienSpeed);

        if (currentLevel >= 3) {
          alien.setVelocityX((Math.random() > 0.5 ? 1 : -1) * 60);
        }
      }
      console.log("spawnAliens: created =>", aliens.countActive(true), "aliens");
    }

    // HELPER: fireBullet
    function fireBullet(x, y) {
      const bullet = bullets.create(x, y - 20, "bullet");
      if (bullet) bullet.setVelocityY(-400);
    }

    // HELPER: loseLife
    function loseLife(scene) {
      setLives(prev => {
        if (prev <= 1) {
          console.log("loseLife: 0 left => game over => reset");
          setGameOver(true);
          scene.scene.restart();
          resetGameState();
          return 0;
        }
        console.log("loseLife: lost life =>", prev - 1);
        return prev - 1;
      });
    }

    // HELPER: resetGameState
    function resetGameState() {
      console.log("resetGameState => everything -> default");
      setScore(0);
      setLives(3);
      setLevel(1);

      scoreValue = 0;
      killCount = 0;
      fireMultiplier = 1;
      trackingMissiles = 0;
      alienSpeed = 60;

      lastAlienShot = 0;
      lastBossShot = 0;
      boss = null;
      setGameOver(false);

      spawnAliens(gameRef.current.scene.scenes[0], 1);
    }

    // HELPER: collectPowerUp
    function collectPowerUp(player, power) {
      power.destroy();
      if (fireMultiplier < 4) {
        fireMultiplier++;
        console.log("collectPowerUp: fireMultiplier =>", fireMultiplier);
      } else if (trackingMissiles < 4) {
        trackingMissiles += 2;
        console.log("collectPowerUp: trackingMissiles =>", trackingMissiles);
      }
    }

    // HELPER: hitAlien
    function hitAlien(bullet, alien) {
      bullet.destroy();
      alien.disableBody(true, true);
      explosionSound.play();

      scoreValue += 10;
      killCount++;
      setScore(scoreValue);

      // 30% chance spawn power-up
      if (Math.random() < 0.3) {
        const power = powerUps.create(alien.x, alien.y, "power");
        power.setVelocityY(100);
      }

      // Next level every 10 kills
      if (killCount % 10 === 0) {
        setLevel(prev => {
          const newLevel = prev + 1;
          console.log("hitAlien: reached newLevel=", newLevel, " => spawnAliens");
          spawnAliens(gameRef.current.scene.scenes[0], newLevel);
          return newLevel;
        });
        alienSpeed += 10;
      }

      // If wave done, no boss => next wave
      if (aliens.countActive(true) === 0 && !boss) {
        console.log("hitAlien: wave cleared => spawn wave at level=", level);
        spawnAliens(gameRef.current.scene.scenes[0], level);
      }
    }

    return () => {
      console.log("Cleanup => destroying phaser instance...");
      gameRef.current?.destroy(true);
      gameRef.current = null;
    };
  }, [gameOver, level]);

  return (
    <div className="phaser-wrapper">
      <Link to="/games" className="back-button">← Back to Lobby</Link>
      <h3 className="react-score">👾 Score: {score}</h3>
      <h4 className="react-level">🧬 Level: {level}</h4>
      <h4 className="react-lives">❤️ Lives: {lives}</h4>
      {gameOver && <h2 className="game-over-text">💀 Game Over!</h2>}
      <div id="phaser-container"></div>
    </div>
  );
}

export default PhaserGame;
